// useKBEntries — fetches /kb-entries with the current filters, exposes refetch().
//
// V1 keeps it simple: re-fetch on filter change, no SWR cache. Inline
// refetch() after mutations works at the V1 KB volume (a few hundred entries
// at most). Mirrors useUsers / useTenants.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ListKBEntriesQuery, ListKBEntriesResponse } from '@gc-coach/shared';
import { listKBEntries } from '../api/endpoints.js';

export interface UseKBEntriesResult {
  data: ListKBEntriesResponse | null;
  isLoading: boolean;
  errorCode: string | null;
  refetch: () => Promise<void>;
}

export function useKBEntries(query: ListKBEntriesQuery): UseKBEntriesResult {
  const [data, setData] = useState<ListKBEntriesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const key = useMemo(
    () =>
      `${query.status ?? ''}|${query.type ?? ''}|${query.domain ?? ''}|${query.language ?? ''}|${query.q?.trim() ?? ''}`,
    [query.status, query.type, query.domain, query.language, query.q],
  );

  const fetchNow = useCallback(async () => {
    setIsLoading(true);
    const result = await listKBEntries(query);
    if (result.ok) {
      setData(result.data);
      setErrorCode(null);
    } else {
      setErrorCode(result.code);
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    void fetchNow();
  }, [fetchNow]);

  return { data, isLoading, errorCode, refetch: fetchNow };
}
